package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.widgets.LocalOnColours
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.BeachAccess
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.leave.LeaveBalances
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.LeaveBalance
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.leave_balance_carry_over
import evoshift.composeapp.generated.resources.leave_balance_entitlement
import evoshift.composeapp.generated.resources.leave_balance_entitlement_set
import evoshift.composeapp.generated.resources.leave_balance_left
import evoshift.composeapp.generated.resources.leave_balance_on_demand
import evoshift.composeapp.generated.resources.leave_balance_pending
import evoshift.composeapp.generated.resources.leave_balance_title
import evoshift.composeapp.generated.resources.leave_balance_used
import evoshift.composeapp.generated.resources.leave_request_days
import evoshift.composeapp.generated.resources.leave_request_exceeds
import evoshift.composeapp.generated.resources.leave_request_exceeds_on_demand
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

const val LEAVE_BALANCE_TAG = "leave_balance"
const val LEAVE_REQUEST_CHECK_TAG = "leave_request_check"

private val WarningRed = Color(0xFFC62828)

/**
 * SH-113: the person's annual leave this year: days left of the entitlement and carry-over, what
 * was taken and is waiting for a decision, and the days on demand. Absences screen (employees).
 */
@Composable
fun LeaveBalanceCard(balance: LeaveBalance, modifier: Modifier = Modifier) {
    RegularCard(modifier = modifier.fillMaxWidth().testTag(LEAVE_BALANCE_TAG)) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.BeachAccess, contentDescription = null, tint = DarkGold)
                Spacer(Modifier.width(8.dp))
                Text(
                    stringResource(Res.string.leave_balance_title, balance.year.toString()),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = DeepNavy
                )
            }
            Text(
                stringResource(Res.string.leave_balance_left, balance.remainingDays, balance.totalDays),
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                color = if (balance.remainingDays < 0) WarningRed else DeepNavy
            )
            if (balance.totalDays > 0) {
                LinearProgressIndicator(
                    progress = { (balance.usedDays.toFloat() / balance.totalDays).coerceIn(0f, 1f) },
                    modifier = Modifier.fillMaxWidth(),
                    color = DarkGold,
                    trackColor = DeepNavy.copy(alpha = 0.1f)
                )
            }
            LeaveFigures(balance)
        }
    }
}

/** The figures behind a balance: entitlement, carry-over, taken, pending, on demand. */
@Composable
fun LeaveFigures(balance: LeaveBalance) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        LeaveRow(
            stringResource(if (balance.entitlementSetByManager) Res.string.leave_balance_entitlement_set else Res.string.leave_balance_entitlement),
            balance.entitlementDays.toString()
        )
        if (balance.carryOverDays > 0) LeaveRow(stringResource(Res.string.leave_balance_carry_over), balance.carryOverDays.toString())
        LeaveRow(stringResource(Res.string.leave_balance_used), balance.usedDays.toString())
        if (balance.pendingDays > 0) LeaveRow(stringResource(Res.string.leave_balance_pending), balance.pendingDays.toString())
        if (balance.onDemandLimit > 0) {
            Text(
                stringResource(Res.string.leave_balance_on_demand, balance.onDemandUsedDays, balance.onDemandLimit),
                style = MaterialTheme.typography.bodySmall,
                color = DeepNavy.copy(alpha = 0.7f)
            )
        }
    }
}

@Composable
private fun LeaveRow(label: String, value: String) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.6f), modifier = Modifier.weight(1f))
        Text(value, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = DeepNavy,
            modifier = Modifier.padding(start = 16.dp), textAlign = TextAlign.End)
    }
}

/**
 * SH-113: a leave request held against the balance in the request dialog: its working days, the
 * days left, and a warning when it asks for more than is left (overall or on demand). Nothing for
 * other absence types or a period outside the balance's year.
 */
@Composable
fun LeaveRequestCheck(balance: LeaveBalance, type: AbsenceType, start: LocalDate?, end: LocalDate?) {
    if (type !in LeaveBalances.LEAVE_TYPES || start == null || end == null || end < start) return
    if (start.year != balance.year && end.year != balance.year) return
    val onDemand = type == AbsenceType.ON_DEMAND
    val days = LeaveBalances.workingDays(balance.market, start, end, balance.year)
    val check = balance.check(days, onDemand)
    // SH-168: in the dialog's on-colour (white on the navy dialog), not navy on navy.
    val on = LocalOnColours.current
    val onColour = on.onSurface
    val warning = on.error
    Column(Modifier.fillMaxWidth().testTag(LEAVE_REQUEST_CHECK_TAG), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(stringResource(Res.string.leave_request_days, days), style = MaterialTheme.typography.bodyMedium, color = onColour)
        Text(
            stringResource(Res.string.leave_balance_left, balance.availableDays, balance.totalDays),
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = onColour
        )
        if (check.exceedsBalance) LeaveWarning(stringResource(Res.string.leave_request_exceeds), warning)
        if (check.exceedsOnDemand) {
            LeaveWarning(stringResource(Res.string.leave_request_exceeds_on_demand, balance.onDemandAvailableDays, balance.onDemandLimit), warning)
        }
    }
}

@Composable
private fun LeaveWarning(text: String, color: Color = WarningRed) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(Icons.Default.Warning, contentDescription = null, tint = color, modifier = Modifier.padding(end = 6.dp))
        Text(text, style = MaterialTheme.typography.bodySmall, color = color)
    }
}
